import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'

import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'
import { useProcessorsList } from '@/lib/api/processors'

import type { PaymentFlowFormData } from './payment-flow-form'
import {
    GROUP_CAPTION_CLASSES,
    GROUP_LABEL_CLASSES,
    emptyRoutingPin,
    pinAmountCurrencies,
} from './processor-routing'
import { RoutingPinRow } from './RoutingPinRow'

type Props = {
    processorIndex: number
    isProcessorDisabled: boolean
}

export const ProcessorRoutingPins = ({ processorIndex, isProcessorDisabled }: Props) => {
    const { t } = useTranslation('paymentFlows')
    const { control } = useFormContext<PaymentFlowFormData>()
    const { data: processorsData } = useProcessorsList()
    const processorId = useWatch({
        control,
        name: `processorConfigs.${processorIndex}.processorId`,
    })
    const processor = (processorsData ?? []).find((item) => item.id === processorId)
    const supportedCurrencies = pinAmountCurrencies(processor?.supportedCurrencies)
    const { fields, append, remove } = useFieldArray({
        control,
        name: `processorConfigs.${processorIndex}.routing.pins`,
    })

    return (
        <div className={cn('space-y-2', isProcessorDisabled && 'opacity-50')}>
            <Separator />
            <div className="space-y-0.5">
                <Label className={GROUP_LABEL_CLASSES}>{t('form.fields.routing.title')}</Label>
                <p className={GROUP_CAPTION_CLASSES}>{t('form.fields.routing.role_caption')}</p>
            </div>
            {fields.map((field, pinIndex) => (
                <Fragment key={field.id}>
                    {/* Conditions are ORed: one matching condition is enough */}
                    {pinIndex > 0 && (
                        <p className={cn(GROUP_LABEL_CLASSES, 'text-center')}>
                            {t('form.fields.routing.or')}
                        </p>
                    )}
                    <RoutingPinRow
                        processorIndex={processorIndex}
                        pinIndex={pinIndex}
                        isProcessorDisabled={isProcessorDisabled}
                        supportedCurrencies={supportedCurrencies}
                        onRemove={() => remove(pinIndex)}
                    />
                </Fragment>
            ))}
            <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => append(emptyRoutingPin())}
                disabled={isProcessorDisabled}
            >
                {t('form.fields.routing.add_row')}
            </Button>
            <p className="text-[10px] text-muted-foreground">{t('form.fields.routing.help')}</p>
        </div>
    )
}
